import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { debounce } from 'utils/helper';
import {
  deleteSwimlaneDetailsItem,
  OVERTIME_COLUMNS,
  userTokenExists,
} from 'utils/common';
import { normalizeCurrencyCode } from 'utils/currencies';

const SWIMLANE_QUERY_KEY = 'get-swimlanes-in-budget';

const getStableRowKey = row => row?.row_id ?? row?.id;

const createDraftRow = ({ selectedItem, type, position, primaryCurrency }) => ({
  row_id: `draft-${type}-${selectedItem?.id}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`,
  row_order: position + 1,
  position,
  item: '',
  quantity: '1',
  multiplier: '1',
  ...OVERTIME_COLUMNS.reduce(
    (values, { slot }) => ({
      ...values,
      [`ot${slot}_quantity`]: '0',
    }),
    {}
  ),
  unit: '',
  multiplier_unit: '',
  rate: '',
  item_total: '0',
  fringe_total: '0',
  column_total: '0',
  actual: null,
  estimate_to_complete_override: null,
  comment: '',
  tags: [],
  account_number: '',
  currency: normalizeCurrencyCode(primaryCurrency),
  is_placeholder: true,
  _isDraft: true,
});

const normalizeRowsWithPositions = rows =>
  rows.map((row, index) => ({
    ...row,
    position: index,
    row_order: index + 1,
  }));

export const getActionRowsInCurrentOrder = (itemOrItems, currentRows) => {
  const requestedRows = Array.isArray(itemOrItems)
    ? itemOrItems
    : [itemOrItems];
  const requestedKeys = new Set(
    requestedRows.map(getStableRowKey).filter(Boolean)
  );
  const orderedRows = currentRows.filter(row =>
    requestedKeys.has(getStableRowKey(row))
  );

  return orderedRows.length ? orderedRows : requestedRows.filter(Boolean);
};

const isCreateLikeRow = row =>
  !row?.id || (typeof row.id === 'string' && row.id.startsWith('temp-'));

const normalizeRateContextItem = value =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

export const getRateContextRefresh = (previousItem, nextItem) => {
  const previous = normalizeRateContextItem(previousItem);
  const next = normalizeRateContextItem(nextItem);

  if (previous.toLocaleLowerCase() === next.toLocaleLowerCase()) return null;

  return {
    // A newly populated row can be fetched on its own and merged into the
    // existing card. Renames and removals need the complete saved context so
    // obsolete roles disappear as well.
    focusItems: previous || !next ? [] : [next],
  };
};

export const prepareSwimlaneDetailMutationDetails = (
  rowData,
  prefillDecimals = data => data
) => {
  const isTempId =
    typeof rowData?.id === 'string' && rowData?.id.startsWith('temp-');
  const isCreate = !rowData?.id || isTempId;
  const filteredRowData = { ...(rowData || {}) };
  const rowOrder = filteredRowData.row_order;
  const createPosition =
    filteredRowData?.position ?? Math.max(0, (rowOrder ?? 1) - 1);

  delete filteredRowData.linked_fringes;
  delete filteredRowData._isPlaceholder;
  delete filteredRowData._isDraft;
  delete filteredRowData._multiEditRevision;
  delete filteredRowData._multiEditField;
  delete filteredRowData.row_id;
  delete filteredRowData.row_order;
  delete filteredRowData.position;
  if (isTempId) {
    delete filteredRowData.id;
  }

  return {
    isCreate,
    details: {
      ...(isCreate
        ? {
            item: '',
            position: createPosition,
          }
        : null),
      ...prefillDecimals(filteredRowData),
      is_placeholder: false,
    },
  };
};

/**
 * Custom hook for managing row operations (add, update, delete, duplicate)
 * with a coalescing mutation queue and optimistic updates with rollback.
 *
 * Coalescing queue: only one mutation per row is in flight at a time.
 * If new edits arrive while a mutation is in flight, the latest data is
 * stored and automatically sent when the current mutation completes.
 * This guarantees no edits are silently dropped.
 */
export const useRowOperations = ({
  selectedItem,
  selectedBudget,
  level,
  type,
  swimlane,
  detailsRows,
  setDetailsRow,
  refetchSwimlaneCosts,
  createSwimlaneDetailsMutation,
  updateSwimlaneDetailsMutation,
  deleteSwimlaneDetailsMutation,
  columns,
  setDirtyRows,
  clearDirtyRow,
  onMoveRow,
  onSuggestionDeleted,
  onRateContextChange,
}) => {
  const queryClient = useQueryClient();
  const pendingDataByRow = useRef(new Map()); // rowKey -> latest data awaiting save
  const inFlightByRow = useRef(new Map()); // rowKey -> boolean
  const debounceByRowRef = useRef(new Map());
  const fireAddRowMutationRef = useRef(null); // breaks circular dep between runMutation ↔ fireAddRowMutation
  const detailsRowsRef = useRef(detailsRows); // always-current detailsRows for onMutate
  const rateContextChangesByRowRef = useRef(new Map());
  const savedRateContextRowsRef = useRef(new Set());
  const committedRateContextRowsRef = useRef(new Set());
  detailsRowsRef.current = detailsRows;

  useEffect(() => {
    rateContextChangesByRowRef.current.clear();
    savedRateContextRowsRef.current.clear();
    committedRateContextRowsRef.current.clear();
  }, [selectedItem?.id]);

  const clearRateContextChange = useCallback(rowKey => {
    rateContextChangesByRowRef.current.delete(rowKey);
    savedRateContextRowsRef.current.delete(rowKey);
    committedRateContextRowsRef.current.delete(rowKey);
  }, []);

  const emitCommittedRateContextChange = useCallback(
    rowKey => {
      const rateContextChange = rateContextChangesByRowRef.current.get(rowKey);
      if (!rateContextChange) {
        clearRateContextChange(rowKey);
        return;
      }

      const refresh = getRateContextRefresh(
        rateContextChange.previousItem,
        rateContextChange.nextItem
      );
      clearRateContextChange(rowKey);
      if (refresh) onRateContextChange?.(refresh);
    },
    [clearRateContextChange, onRateContextChange]
  );

  const id =
    level === 0
      ? selectedBudget?.budget
      : level === 1
      ? selectedItem?.swimlane
      : selectedItem?.parent;

  const queryKey = useMemo(
    () => [SWIMLANE_QUERY_KEY, { id, type }],
    [id, type]
  );

  const prefillDecimals = useCallback(
    rowData =>
      Object.keys(rowData).reduce((acc, fieldKey) => {
        const col = columns.find(c => c.key === fieldKey);
        const isDecimal = col?.dataType === 'decimal';
        if (isDecimal && !acc[fieldKey] && acc[fieldKey] !== 0) {
          acc[fieldKey] =
            col.defaultValue !== undefined && col.defaultValue !== ''
              ? col.defaultValue
              : acc[fieldKey];
        }
        return acc;
      }, rowData),
    [columns]
  );

  const runMutation = useCallback(
    (rowData, mutateMethod, payload, options = {}) => {
      const rowKey = getStableRowKey(rowData);

      mutateMethod.mutate(payload, {
        onMutate: async () => {
          // Cancel in-flight refetches to prevent stale server data from
          // overwriting the optimistic local state that addRow already applied.
          await queryClient.cancelQueries(queryKey);
          // Save snapshot for rollback on error (best-effort via ref)
          return {
            previousRows: detailsRowsRef.current[selectedItem?.id] || [],
          };
        },
        onSuccess: response => {
          const serverData = response?.data || {};
          const hasPending = pendingDataByRow.current.has(rowKey);

          // Targeted update: only touch server-managed fields so we never
          // overwrite display values the user edited while the mutation was
          // in-flight.  For rows with no pending edits the full server
          // response is safe to apply.
          setDetailsRow(prev => ({
            ...prev,
            [selectedItem?.id]: (prev[selectedItem?.id] || []).map(row => {
              if (getStableRowKey(row) !== rowKey) return row;

              if (hasPending) {
                // User edited while in-flight — only update metadata.
                // Recompute column_total from the local item_total (which
                // reflects the user's latest edits) + the server's
                // fringe_total (authoritative, server-computed).
                const updatedFringeTotal =
                  serverData.fringe_total ?? row.fringe_total;
                return {
                  ...row,
                  id: serverData.id ?? row.id,
                  updated_on: serverData.updated_on,
                  is_placeholder: false,
                  _isDraft: false,
                  fringe_total: updatedFringeTotal,
                  column_total:
                    parseFloat(row.item_total || 0) +
                    parseFloat(updatedFringeTotal || 0),
                };
              }
              // No pending edits — safe to apply full server response
              return {
                ...row,
                ...serverData,
                row_id: row.row_id,
                row_order: row.row_order,
                position: row.position,
                is_placeholder: false,
                _isDraft: false,
              };
            }),
          }));

          // Propagate real id for creates into any pending data
          const pending = pendingDataByRow.current.get(rowKey);
          if (pending && serverData?.id) {
            pendingDataByRow.current.set(rowKey, {
              ...pending,
              id: serverData.id,
            });
          }

          // If new data arrived while this mutation was in flight, fire another
          if (pendingDataByRow.current.has(rowKey)) {
            const nextData = pendingDataByRow.current.get(rowKey);
            pendingDataByRow.current.delete(rowKey);
            inFlightByRow.current.delete(rowKey);
            // Fire directly — data is already coalesced, skip debounce
            fireAddRowMutationRef.current?.(nextData);
          } else {
            // No more pending edits — mark clean and refresh swimlane costs
            // + budget total. The detail view rows are unaffected because they
            // are driven by local detailsRows state, not the swimlane query.
            inFlightByRow.current.delete(rowKey);
            if (clearDirtyRow) {
              if (rowKey) clearDirtyRow(rowKey);
              if (rowData?.id) clearDirtyRow(rowData.id);
              if (serverData?.id) clearDirtyRow(serverData.id);
            }
            refetchSwimlaneCosts?.({ budgetId: selectedBudget?.budget });
            if (rateContextChangesByRowRef.current.has(rowKey)) {
              savedRateContextRowsRef.current.add(rowKey);
              if (committedRateContextRowsRef.current.has(rowKey)) {
                emitCommittedRateContextChange(rowKey);
              }
            }
          }
        },
        onError: (_err, _variables, context) => {
          inFlightByRow.current.delete(rowKey);

          // Only discard pending data for client errors (4xx) which indicate
          // the data itself is invalid. For transient errors (network, 5xx),
          // keep pending data so it gets retried on the next user edit.
          const statusCode = _err?.response?.status;
          const isClientError = statusCode >= 400 && statusCode < 500;
          savedRateContextRowsRef.current.delete(rowKey);
          if (isClientError) {
            pendingDataByRow.current.delete(rowKey);
            clearRateContextChange(rowKey);
          }

          if (context?.previousRows != null) {
            const isFailedDraftCreate = !rowData?.id && rowData?.row_id;
            const previousRows = isFailedDraftCreate
              ? context.previousRows.filter(
                  row => getStableRowKey(row) === rowKey || !row?.is_placeholder
                )
              : context.previousRows;

            setDetailsRow(prev => ({
              ...prev,
              [selectedItem?.id]: previousRows,
            }));
          }
          if (clearDirtyRow) {
            if (rowKey) clearDirtyRow(rowKey);
            if (rowData?.id) clearDirtyRow(rowData.id);
          }
          toast.error('Failed to save changes. Please try again.');
        },
        ...options,
      });
    },
    [
      queryClient,
      queryKey,
      selectedItem,
      setDetailsRow,
      selectedBudget?.budget,
      clearDirtyRow,
      refetchSwimlaneCosts,
      clearRateContextChange,
      emitCommittedRateContextChange,
    ]
  );

  const fireAddRowMutation = useCallback(
    rowData => {
      const rowKey = getStableRowKey(rowData);

      // If a mutation is already in flight for this row, coalesce:
      // store the latest data and it will be picked up when the in-flight mutation completes.
      if (inFlightByRow.current.get(rowKey)) {
        pendingDataByRow.current.set(rowKey, rowData);
        return;
      }

      const { isCreate, details } = prepareSwimlaneDetailMutationDetails(
        rowData,
        prefillDecimals
      );
      const mutateMethod = isCreate
        ? createSwimlaneDetailsMutation
        : updateSwimlaneDetailsMutation;
      const payload = {
        details,
        selectedItem,
        swimlane,
        type,
      };

      inFlightByRow.current.set(rowKey, true);
      runMutation(rowData, mutateMethod, payload);
    },
    [
      prefillDecimals,
      selectedItem,
      swimlane,
      type,
      createSwimlaneDetailsMutation,
      updateSwimlaneDetailsMutation,
      runMutation,
    ]
  );

  // Keep ref in sync so runMutation's onSuccess can call fireAddRowMutation
  // without a circular useCallback dependency
  fireAddRowMutationRef.current = fireAddRowMutation;

  // Flush all pending debounced mutations on unmount (navigation) and on
  // page refresh/close (beforeunload). React cleanup only runs on unmount,
  // not on hard refresh — beforeunload covers that case.
  // On logout the auth token is already cleared, so we cancel instead of
  // flushing to avoid 401 errors and stale mutation toasts.
  useEffect(() => {
    const debounceMap = debounceByRowRef.current;
    const flushAll = () => {
      for (const debouncedFn of debounceMap.values()) {
        debouncedFn.flush?.();
      }
    };
    const cancelAll = () => {
      for (const debouncedFn of debounceMap.values()) {
        debouncedFn.cancel?.();
      }
      debounceMap.clear();
      pendingDataByRow.current.clear();
      inFlightByRow.current.clear();
    };
    window.addEventListener('beforeunload', flushAll);
    return () => {
      window.removeEventListener('beforeunload', flushAll);
      if (userTokenExists()) {
        flushAll();
      } else {
        cancelAll();
      }
    };
  }, []);

  const handleAddRow = useCallback(
    rowData => {
      const rowKey = getStableRowKey(rowData);
      let debouncedFn = debounceByRowRef.current.get(rowKey);
      if (!debouncedFn) {
        // Use ref so the debounced function always calls the latest fireAddRowMutation,
        // even if detailsRows changed and fireAddRowMutation was recreated.
        debouncedFn = debounce(
          data => fireAddRowMutationRef.current?.(data),
          500
        );
        debounceByRowRef.current.set(rowKey, debouncedFn);
      }
      debouncedFn(rowData);
    },
    [] // no deps — fireAddRowMutationRef is a stable ref
  );

  const addRow = useCallback(
    rowData => {
      const dataWithFlag = {
        ...rowData,
        is_placeholder: false,
        _isDraft: false,
      };
      const initialRowKey = getStableRowKey(dataWithFlag);
      const currentRows = detailsRowsRef.current[selectedItem?.id] || [];
      const visualIndex = currentRows.findIndex(
        row => getStableRowKey(row) === initialRowKey
      );
      const dataToSave =
        isCreateLikeRow(dataWithFlag) && visualIndex >= 0
          ? {
              ...dataWithFlag,
              position: visualIndex,
              row_order: visualIndex + 1,
            }
          : dataWithFlag;
      const rowKey = getStableRowKey(dataToSave);
      const currentRow = currentRows.find(
        row => getStableRowKey(row) === rowKey
      );
      const trackedRateContextChange =
        rateContextChangesByRowRef.current.get(rowKey);
      const previousItem =
        trackedRateContextChange?.previousItem ?? currentRow?.item;
      const nextItem = dataToSave?.item;
      if (getRateContextRefresh(previousItem, nextItem)) {
        rateContextChangesByRowRef.current.set(rowKey, {
          previousItem,
          nextItem,
        });
        savedRateContextRowsRef.current.delete(rowKey);
      } else {
        clearRateContextChange(rowKey);
      }
      if (setDirtyRows && rowKey) {
        setDirtyRows(prev => new Set(prev).add(rowKey));
      }

      // Update local state immediately for responsiveness
      setDetailsRow(prev => {
        const rows = prev[selectedItem?.id] || [];
        if (!rows.some(item => getStableRowKey(item) === rowKey)) {
          return prev;
        }

        return {
          ...prev,
          [selectedItem?.id]: rows.map(item => {
            if (getStableRowKey(item) === rowKey) {
              const updatedRow = { ...item, ...dataToSave };
              if (dataToSave.fringe_total === undefined) {
                updatedRow.fringe_total = item.fringe_total;
              }
              updatedRow.row_id = item.row_id ?? dataToSave.row_id;
              return updatedRow;
            }
            return item;
          }),
        };
      });

      // Dispatch to debounced mutation pipeline — coalescing queue handles in-flight conflicts
      handleAddRow(dataToSave);
    },
    [
      clearRateContextChange,
      handleAddRow,
      selectedItem,
      setDirtyRows,
      setDetailsRow,
    ]
  );

  const commitRateContextChange = useCallback(
    ({ row, previousItem, nextItem } = {}) => {
      const rowKey = getStableRowKey(row);
      if (!rowKey) return;

      const trackedChange = rateContextChangesByRowRef.current.get(rowKey);
      const initialItem = trackedChange?.previousItem ?? previousItem;
      const currentItem = trackedChange?.nextItem ?? nextItem;
      if (!getRateContextRefresh(initialItem, currentItem)) {
        clearRateContextChange(rowKey);
        return;
      }

      rateContextChangesByRowRef.current.set(rowKey, {
        previousItem: initialItem,
        nextItem: currentItem,
      });
      committedRateContextRowsRef.current.add(rowKey);

      // Leaving the Item cell is also the right time to flush its pending save.
      // The successful mutation will emit the refresh after the database contains
      // the new role; if it already saved while the user paused, emit immediately.
      debounceByRowRef.current.get(rowKey)?.flush?.();
      if (savedRateContextRowsRef.current.has(rowKey)) {
        emitCommittedRateContextChange(rowKey);
      }
    },
    [clearRateContextChange, emitCommittedRateContextChange]
  );

  // Creates a local-only draft row. It is promoted to a backend row by addRow
  // after the user types in any field or adds a fringe.
  const addEmptyRow = useCallback(
    excludeRowId => {
      if (!selectedItem?.id) return;

      // Check if a placeholder row already exists to prevent duplicates.
      const currentRows = detailsRowsRef.current[selectedItem?.id] || [];
      const hasPlaceholder = currentRows.some(
        row =>
          row && getStableRowKey(row) !== excludeRowId && row.is_placeholder
      );
      if (hasPlaceholder) return;

      setDetailsRow(prev => {
        const prevRows = prev[selectedItem?.id] || [];
        const alreadyHasPlaceholder = prevRows.some(
          row =>
            row && getStableRowKey(row) !== excludeRowId && row.is_placeholder
        );
        if (alreadyHasPlaceholder) return prev;

        const draftRow = createDraftRow({
          selectedItem,
          type,
          position: prevRows.length,
          primaryCurrency: selectedBudget?.primary_currency,
        });

        return {
          ...prev,
          [selectedItem?.id]: [...prevRows, draftRow],
        };
      });
    },
    [selectedItem, type, setDetailsRow, selectedBudget?.primary_currency]
  );

  const insertEmptyRow = useCallback(
    (item, placement) => {
      if (!selectedItem?.id) return;

      const itemKey = getStableRowKey(item);
      const currentRows = detailsRows[selectedItem.id] || [];
      const currentIndex = currentRows.findIndex(
        row => getStableRowKey(row) === itemKey
      );
      const insertIndex =
        currentIndex === -1
          ? currentRows.length
          : currentIndex + (placement === 'below' ? 1 : 0);
      const optimisticRow = {
        ...createDraftRow({
          selectedItem,
          type,
          position: insertIndex,
          primaryCurrency: selectedBudget?.primary_currency,
        }),
        is_placeholder: false,
        _isDraft: false,
      };
      const nextRows = [...currentRows];
      nextRows.splice(insertIndex, 0, optimisticRow);
      const optimisticRows = normalizeRowsWithPositions(nextRows);

      setDetailsRow(prev => ({
        ...prev,
        [selectedItem.id]: optimisticRows,
      }));

      fireAddRowMutationRef.current?.(optimisticRows[insertIndex]);
    },
    [selectedItem, type, setDetailsRow, detailsRows, selectedBudget]
  );

  const insertMultipleEmptyRows = useCallback(
    (items, placement) => {
      if (!selectedItem?.id) return;

      const currentRows = detailsRows[selectedItem.id] || [];
      const actionRows = getActionRowsInCurrentOrder(items, currentRows);
      if (actionRows.length < 2) {
        insertEmptyRow(actionRows[0], placement);
        return;
      }

      const actionRowKeys = new Set(actionRows.map(getStableRowKey));
      const actionRowIndices = currentRows
        .map((row, index) =>
          actionRowKeys.has(getStableRowKey(row)) ? index : -1
        )
        .filter(index => index >= 0);
      const insertIndex =
        placement === 'below'
          ? Math.max(...actionRowIndices) + 1
          : Math.min(...actionRowIndices);
      const newRows = Array.from(
        { length: actionRows.length },
        (_, offset) => ({
          ...createDraftRow({
            selectedItem,
            type,
            position: insertIndex + offset,
            primaryCurrency: selectedBudget?.primary_currency,
          }),
          is_placeholder: false,
          _isDraft: false,
        })
      );
      const optimisticRows = normalizeRowsWithPositions([
        ...currentRows.slice(0, insertIndex),
        ...newRows,
        ...currentRows.slice(insertIndex),
      ]);

      setDetailsRow(prev => ({
        ...prev,
        [selectedItem.id]: optimisticRows,
      }));

      optimisticRows
        .slice(insertIndex, insertIndex + newRows.length)
        .forEach(row => fireAddRowMutationRef.current?.(row));
    },
    [
      detailsRows,
      insertEmptyRow,
      selectedBudget?.primary_currency,
      selectedItem,
      setDetailsRow,
      type,
    ]
  );

  const deleteMultipleRows = useCallback(
    actionRows => {
      const previousRows = detailsRows[selectedItem?.id] || [];
      const rowsToDelete = getActionRowsInCurrentOrder(
        actionRows,
        previousRows
      );
      if (rowsToDelete.length < 2) return false;

      const rowKeys = new Set(rowsToDelete.map(getStableRowKey));
      const remainingRows = normalizeRowsWithPositions(
        previousRows.filter(row => !rowKeys.has(getStableRowKey(row)))
      );
      setDetailsRow(prev => ({
        ...prev,
        [selectedItem?.id]: remainingRows,
      }));

      Promise.all(
        rowsToDelete.map(
          row =>
            new Promise((resolve, reject) => {
              deleteSwimlaneDetailsMutation.mutate(
                { type, id: row.id, signal: undefined },
                { onSuccess: resolve, onError: reject }
              );
            })
        )
      )
        .then(() => {
          rowsToDelete.forEach(row => {
            deleteSwimlaneDetailsItem(queryClient, row, selectedItem, {
              type,
              id,
            });
            clearDirtyRow?.(row.id);
            onSuggestionDeleted?.(row.item, `detail:${selectedItem?.id || ''}`);
          });
          refetchSwimlaneCosts?.({ budgetId: selectedBudget?.budget });
          if (rowsToDelete.some(row => normalizeRateContextItem(row.item))) {
            onRateContextChange?.({ focusItems: [] });
          }
          queryClient.invalidateQueries({
            queryKey: [SWIMLANE_QUERY_KEY],
            exact: false,
          });
        })
        .catch(() => {
          setDetailsRow(prev => ({
            ...prev,
            [selectedItem?.id]: previousRows,
          }));
          queryClient.invalidateQueries({
            queryKey: [SWIMLANE_QUERY_KEY],
            exact: false,
          });
          toast.error(
            'Unable to delete all selected line items. Please try again.'
          );
        });

      return true;
    },
    [
      clearDirtyRow,
      deleteSwimlaneDetailsMutation,
      detailsRows,
      id,
      onRateContextChange,
      onSuggestionDeleted,
      queryClient,
      refetchSwimlaneCosts,
      selectedBudget?.budget,
      selectedItem,
      setDetailsRow,
      type,
    ]
  );

  const duplicateMultipleRows = useCallback(
    async actionRows => {
      const currentRows = detailsRows[selectedItem?.id] || [];
      const rowsToDuplicate = getActionRowsInCurrentOrder(
        actionRows,
        currentRows
      );
      if (rowsToDuplicate.length < 2) return false;

      const rowKeys = new Set(rowsToDuplicate.map(getStableRowKey));
      const insertIndex =
        currentRows.reduce(
          (lastIndex, row, index) =>
            rowKeys.has(getStableRowKey(row)) ? index : lastIndex,
          -1
        ) + 1;
      const createdRows = [];

      try {
        for (const [offset, row] of rowsToDuplicate.entries()) {
          // Serializing these creates preserves the selected rows' order at
          // the insertion point while the API shifts later positions.
          // eslint-disable-next-line no-await-in-loop
          const response = await new Promise((resolve, reject) => {
            createSwimlaneDetailsMutation.mutate(
              {
                details: {
                  ...row,
                  id: null,
                  position: insertIndex + offset,
                  rate: row?.rate?.replace?.(/,/g, '') ?? row?.rate,
                },
                selectedItem,
                swimlane,
                type,
              },
              { onSuccess: resolve, onError: reject }
            );
          });
          const serverRow = response?.data || {};
          createdRows.push({
            ...serverRow,
            id:
              serverRow.id ||
              `temp-${type}-${selectedItem?.id}-${Date.now()}-${offset}`,
          });
        }

        setDetailsRow(prev => {
          const latestRows = prev[selectedItem?.id] || [];
          const latestInsertIndex =
            latestRows.reduce(
              (lastIndex, row, index) =>
                rowKeys.has(getStableRowKey(row)) ? index : lastIndex,
              -1
            ) + 1;
          return {
            ...prev,
            [selectedItem?.id]: normalizeRowsWithPositions([
              ...latestRows.slice(0, latestInsertIndex),
              ...createdRows,
              ...latestRows.slice(latestInsertIndex),
            ]),
          };
        });
        refetchSwimlaneCosts?.({ budgetId: selectedBudget?.budget });
        if (rowsToDuplicate.some(row => normalizeRateContextItem(row.item))) {
          onRateContextChange?.({ focusItems: [] });
        }
      } catch (_error) {
        queryClient.invalidateQueries({
          queryKey: [SWIMLANE_QUERY_KEY],
          exact: false,
        });
        toast.error(
          'Unable to duplicate all selected line items. Please try again.'
        );
      }

      return true;
    },
    [
      createSwimlaneDetailsMutation,
      detailsRows,
      onRateContextChange,
      queryClient,
      refetchSwimlaneCosts,
      selectedBudget?.budget,
      selectedItem,
      setDetailsRow,
      swimlane,
      type,
    ]
  );

  const handleRowAction = useCallback(
    itemOrItems => action => {
      const currentRows = detailsRows[selectedItem?.id] || [];
      const actionRows = getActionRowsInCurrentOrder(itemOrItems, currentRows);
      const isMultipleRows = actionRows.length > 1;
      const item = actionRows[0] || itemOrItems;

      if (action === 'insert-above-item') {
        if (isMultipleRows) insertMultipleEmptyRows(actionRows, 'above');
        else insertEmptyRow(item, 'above');
        return;
      }

      if (action === 'insert-below-item') {
        if (isMultipleRows) insertMultipleEmptyRows(actionRows, 'below');
        else insertEmptyRow(item, 'below');
        return;
      }

      if (action === 'move-item') {
        onMoveRow?.(isMultipleRows ? actionRows : item);
        return;
      }

      if (action === 'delete-item') {
        if (isMultipleRows && deleteMultipleRows(actionRows)) return;

        const payload = { type, id: item?.id };
        const previousRows = currentRows;
        const itemKey = getStableRowKey(item);
        const newDetails =
          previousRows.filter(row => getStableRowKey(row) !== itemKey) || [];
        const adjustedRows = normalizeRowsWithPositions(newDetails);

        setDetailsRow(prev => ({
          ...prev,
          [selectedItem?.id]: adjustedRows,
        }));

        if (item?.id) {
          deleteSwimlaneDetailsMutation.mutate(
            { ...payload, signal: undefined },
            {
              onSuccess: async () => {
                deleteSwimlaneDetailsItem(queryClient, item, selectedItem, {
                  type,
                  id,
                });
                if (clearDirtyRow) clearDirtyRow(item.id);
                refetchSwimlaneCosts?.({ budgetId: selectedBudget?.budget });
                onSuggestionDeleted?.(
                  item?.item,
                  `detail:${selectedItem?.id || ''}`
                );
                if (normalizeRateContextItem(item?.item)) {
                  onRateContextChange?.({ focusItems: [] });
                }
              },
              onError: () => {
                setDetailsRow(prev => ({
                  ...prev,
                  [selectedItem?.id]: previousRows,
                }));
                toast.error('Failed to delete. Please try again.');
              },
            }
          );
        }
      }

      if (action === 'duplicate-item') {
        if (isMultipleRows) {
          duplicateMultipleRows(actionRows);
          return;
        }

        const index = detailsRows[selectedItem?.id].findIndex(
          row => getStableRowKey(row) === getStableRowKey(item)
        );
        const newRowId = `temp-${type}-${selectedItem?.id}-${Date.now()}`;

        const payload = {
          details: {
            ...item,
            id: null,
            position: index + 1,
            rate: item?.rate?.replace?.(/,/g, '') ?? item?.rate,
          },
          selectedItem,
          swimlane,
          type,
        };

        createSwimlaneDetailsMutation.mutate(payload, {
          onSuccess: async response => {
            const newRow = {
              ...response.data,
              id: response.data?.id || newRowId,
            };
            setDetailsRow(prev => {
              const updatedRows = [...prev[selectedItem?.id]];
              updatedRows.splice(index + 1, 0, newRow);
              const adjustedRows = normalizeRowsWithPositions(updatedRows);
              return { ...prev, [selectedItem?.id]: adjustedRows };
            });
            refetchSwimlaneCosts?.({ budgetId: selectedBudget?.budget });
          },
          onError: () => toast.error('Error in duplicating item'),
        });
      }
    },
    [
      deleteSwimlaneDetailsMutation,
      refetchSwimlaneCosts,
      selectedItem,
      type,
      id,
      queryClient,
      createSwimlaneDetailsMutation,
      swimlane,
      detailsRows,
      setDetailsRow,
      selectedBudget?.budget,
      clearDirtyRow,
      insertEmptyRow,
      insertMultipleEmptyRows,
      deleteMultipleRows,
      duplicateMultipleRows,
      onMoveRow,
      onSuggestionDeleted,
      onRateContextChange,
    ]
  );

  return {
    addRow,
    addEmptyRow,
    handleRowAction,
    handleAddRow,
    commitRateContextChange,
  };
};
